import { useState } from "react";
import { Button } from "../../../../components/buttons/button";
import { ProfileIcon } from "../../../../components/cards/profilePic";
import { Icon } from "../../../../components/icon/icon";
import { BgWrapper } from "../../../../components/wrapper/bgWrapper";
import { CreatedEvent } from "../../../../redux/features/event/event.types";
import {
  copyTextToClipboard,
  getEventUrl,
  handleShare,
} from "../../../../utils/helpers";

export const PreviewOrganizerDetails = ({
  event,
  className = "",
  canShare,
  showSocials = false,
}: {
  event?: CreatedEvent;
  className?: string;
  canShare: boolean;
  showSocials?: boolean;
}) => {
  const [buttonText, setButtonText] = useState("Copy Link");

  const handleCopy = async () => {
    await copyTextToClipboard(getEventUrl(event?.slug ?? ""));
    setButtonText("Copied!");
    setTimeout(() => {
      setButtonText("Copy Link");
    }, 2000);
  };

  const orgaizerSocials = [
    {
      handle: "Email",
      url: `mailto:${event?.createdBy?.organisation?.email}`,
      icon: "email-colored",
      visible: !!event?.createdBy?.organisation?.email,
    },
    {
      handle: "Instagram",
      url: event?.createdBy?.instagramLink,
      icon: "instagram-colored",
      visible: !!event?.createdBy?.instagramLink,
    },
    {
      handle: "X",
      url: event?.createdBy?.twitterLink,
      icon: "twitter-colored",
      visible: !!event?.createdBy?.twitterLink,
    },
    {
      handle: "Website",
      url: event?.createdBy?.website,
      icon: "globe-colored",
      visible: !!event?.createdBy?.website,
    },
  ];

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      <div className="order-last grid grid-cols-2 items-center gap-3 md:order-first">
        <Button
          type="button"
          onClick={handleCopy}
          icon="link"
          text={buttonText}
          variation="white"
          className="!rounded-2xl !p-3"
          disabled={!canShare}
        />

        <Button
          type="button"
          onClick={async () => {
            await handleShare("", "", getEventUrl(event?.slug ?? ""));
          }}
          icon="upload"
          text={"Share Event"}
          variation="white"
          className="!rounded-2xl !p-3"
          disabled={!canShare}
        />
      </div>

      <BgWrapper className="flex flex-col gap-3.5">
        <p className="w-full border-b pb-2 text-sm text-grey-500">
          {event?.createdBy?.isOrganisation ? "Organized by" : "Organized by"}
        </p>

        <div className="flex items-center gap-[6px]">
          {event?.createdBy?.isOrganisation ? (
            <img
              alt="organization logo"
              src={event?.createdBy?.organisation?.logo}
              className={`max-h-[24px] min-h-[24px] min-w-[24px] max-w-[24px] overflow-hidden rounded-[4px] object-cover ${!event?.createdBy?.organisation?.logo && "hidden"}`}
            />
          ) : (
            <ProfileIcon dimension={24} className="!p-[4.5px]" />
          )}

          <p className="text-base font-bold text-primary">
            {event?.createdBy?.isOrganisation
              ? `${event?.createdBy?.organisation?.name}`
              : `${event?.createdBy?.firstName} ${event?.createdBy?.lastName}`}
          </p>
        </div>

        {!event?.createdBy?.isOrganisation ? (
          <>
            {event?.coOrganizers
              ?.filter(
                (co) => co.role === "manager" && co.status === "accepted",
              )
              ?.map((co) => {
                return (
                  <div key={co.email} className="flex items-center gap-[6px]">
                    <ProfileIcon dimension={24} className="!p-[4.5px]" />

                    <p className="text-base font-bold text-primary">
                      {`${co?.userId?.firstName} ${co?.userId?.lastName}`}
                    </p>
                  </div>
                );
              })}
          </>
        ) : undefined}
      </BgWrapper>

      {showSocials ? (
        <div className="flex flex-wrap items-center gap-3">
          {orgaizerSocials.map((os, idx) => {
            return os.visible ? (
              <a
                key={`${idx}`}
                href={os.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 rounded-2xl bg-white px-3 py-[10px] text-sm font-bold text-primary"
              >
                <Icon svg={os.icon} width={24} height={24} />

                <span>{os.handle}</span>
              </a>
            ) : undefined;
          })}
        </div>
      ) : undefined}
    </div>
  );
};
